import { asyncAgentQuestions, formatAsyncQuestionAnswer, type AsyncQuestionAnswer, type ChatMessage, type CodexNotification } from '@reados/contracts';
import type { TranscriptPayload } from './projection.ts';

export type TranscriptAsyncQuestionAnswer = AsyncQuestionAnswer;

/** Only an application acceptance receipt for this exact native source marks a
 * question answered. Later user prose and item completion are not receipts. */
export function asyncQuestionWasAnswered(message: ChatMessage, events: CodexNotification[]): boolean {
  if (!asyncAgentQuestions(message) || typeof message.threadId !== 'string' || typeof message.turnId !== 'string') return false;
  return events.some(event => {
    const params = event.params;
    return event.method === 'reados/asyncQuestionAnswered' && params && typeof params === 'object'
      && params.threadId === message.threadId && params.turnId === message.turnId && params.messageID === message.id
      && typeof params.ownerThreadId === 'string' && !!params.ownerThreadId
      && typeof params.requestId === 'string' && !!params.requestId
      && typeof params.answerSha256 === 'string' && /^[a-f0-9]{64}$/.test(params.answerSha256)
      && typeof params.acceptedAtMilliseconds === 'number' && Number.isFinite(params.acceptedAtMilliseconds) && params.acceptedAtMilliseconds > 0;
  });
}

/** The opaque iframe names an original question, never its native destination.
 * Re-resolve source metadata and visible ownership from the parent's catalog. */
export function resolveTranscriptAsyncQuestionAnswer(messageID: string, answers: string[], messages: ChatMessage[], payload: TranscriptPayload): TranscriptAsyncQuestionAnswer | null {
  const sources = messages.filter(message => message.id === messageID);
  if (sources.length !== 1) return null;
  const source = sources[0], questions = asyncAgentQuestions(source);
  if (!questions || !formatAsyncQuestionAnswer(source, answers)
    || typeof source.threadId !== 'string' || !source.threadId || typeof source.turnId !== 'string' || !source.turnId) return null;
  const blocks = payload.blockCatalog.filter(block => block.type === 'reados_async_question' && block.sourceBlockId === messageID);
  if (blocks.length !== 1) return null;
  const block = blocks[0];
  if (block.readosAsyncDelivery !== true || block.readosAsyncQuestionAnswered === true || block.text !== source.text
    || JSON.stringify(block.readosAsyncQuestions) !== JSON.stringify(questions)) return null;
  const article = payload.messages.find(message => message.id === block.messageID && message.role === 'assistant'
    && message.threadId === source.threadId && message.readexCodexTurnID === source.turnId
    && Array.isArray(message.blocks) && message.blocks.includes(block.id));
  return article ? { messageID, threadId: source.threadId, turnId: source.turnId, answers: answers.map(answer => answer.trim()) } : null;
}
